import axios, { endPoints } from 'utils/api';

// Async health-data export slice (Plan 06). The trigger endpoint only enqueues a
// job; the CSV download URL arrives later, so the FE keeps a list of the caller's
// recent exports and polls `show` for the one currently being built.

const SET_LIST = 'healthDataExports/SET_LIST';
const SET_LOADING = 'healthDataExports/SET_LOADING';
const SET_ERROR = 'healthDataExports/SET_ERROR';
const UPSERT_ONE = 'healthDataExports/UPSERT_ONE';
const SET_TRIGGERING = 'healthDataExports/SET_TRIGGERING';
const SET_RATE_LIMIT = 'healthDataExports/SET_RATE_LIMIT';

export const setExportsList = (data) => ({ type: SET_LIST, data });
export const setExportsLoading = (loading) => ({ type: SET_LOADING, loading });
export const setExportsError = (error) => ({ type: SET_ERROR, error });
export const upsertExport = (row) => ({ type: UPSERT_ONE, row });
export const setTriggering = (triggering) => ({ type: SET_TRIGGERING, triggering });
export const setRateLimitUntil = (rateLimitUntil) => ({ type: SET_RATE_LIMIT, rateLimitUntil });

const INITIAL_STATE = {
    data: [],
    loading: false,
    error: null,
    triggering: false,
    // epoch ms until which the trigger endpoint is rate-limited (429), or null.
    rateLimitUntil: null
};

function mergeRow(list, row) {
    if (!row || !row.id) return list;
    const idx = list.findIndex((r) => r.id === row.id);

    if (idx === -1) return [row, ...list];

    const next = [...list];

    next[idx] = { ...next[idx], ...row };

    return next;
}

// eslint-disable-next-line import/no-anonymous-default-export
export default function(state = INITIAL_STATE, action) {
    switch (action.type) {
        case SET_LIST:
            return { ...state, data: [...action.data], error: null };

        case SET_LOADING:
            return { ...state, loading: action.loading };

        case SET_ERROR:
            return { ...state, error: action.error, loading: false };

        case UPSERT_ONE:
            return { ...state, data: mergeRow(state.data, action.row) };

        case SET_TRIGGERING:
            return { ...state, triggering: action.triggering };

        case SET_RATE_LIMIT:
            return { ...state, rateLimitUntil: action.rateLimitUntil };

        default:
            return state;
    }
}

// ── Thunks ─────────────────────────────────────────────────────────────────

// GET list of the caller's recent exports.
export const fetchExports = (params = {}) => async (dispatch) => {
    dispatch(setExportsLoading(true));

    try {
        const query = {};

        if (params.page) query.page = params.page;
        if (params.pageSize) query.pageSize = params.pageSize;

        const response = await axios.get(endPoints.healthDataExports, { params: query });
        const rows = response.data?.data ?? response.data ?? [];

        dispatch(setExportsList(Array.isArray(rows) ? rows : []));
    } catch (error) {
        dispatch(setExportsError(
            error.response?.data?.message || error.message || 'Failed to load exports'
        ));
    } finally {
        dispatch(setExportsLoading(false));
    }
};

// POST a new export. Returns the queued row on success. Throws on validation (422)
// so the caller can surface field errors; records the rate-limit window on 429.
export const triggerExport = (filters = {}) => async (dispatch) => {
    dispatch(setTriggering(true));

    try {
        const response = await axios.post(endPoints.healthDataExports, filters);

        dispatch(upsertExport(response.data));

        return response.data;
    } catch (error) {
        if (error.response?.status === 429) {
            const retryAfter = Number(error.response.headers?.['retry-after']) || 3600;

            dispatch(setRateLimitUntil(Date.now() + retryAfter * 1000));
        }

        throw error;
    } finally {
        dispatch(setTriggering(false));
    }
};

// GET one export by UUID — used for polling and for re-signing a fresh URL.
export const fetchExport = (id) => async (dispatch) => {
    const response = await axios.get(`${endPoints.healthDataExports}/${id}`);

    dispatch(upsertExport(response.data));

    return response.data;
};
